import { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, Badge } from '@/components/common';
import { GlobeAltIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/store';
import { useOwnLookupPlan, useSaveOwnLookupPlan } from '@/hooks/useIntegrations';
import api, { getErrorMessage } from '@/services/api';
import toast from 'react-hot-toast';
import { formatCurrency } from '@/utils';

interface PendingVerification {
  domain: string;
  verification_token: string;
  created_at: string;
}

interface VerificationInstructions {
  message: string;
  verification_token: string;
  instructions: string;
}

export function DomainSettingsPage() {
  const { user } = useAuthStore();
  const [currentDomain, setCurrentDomain] = useState<string | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [pendingVerifications, setPendingVerifications] = useState<PendingVerification[]>([]);
  const [verificationInstructions, setVerificationInstructions] = useState<VerificationInstructions | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const canManageDomain = user?.role === 'broker_admin' || user?.role === 'super_admin';

  const loadDomainSettings = useCallback(async () => {
    try {
      const response = await api.get('/brokers/domains/current_domain/');
      setCurrentDomain(response.data.custom_domain || null);
      setPendingVerifications(response.data.pending_verifications || []);
    } catch {
      // Silently handle - user may not have a broker associated
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDomainSettings();
  }, [loadDomainSettings]);

  const handleRequestDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    setIsRequesting(true);
    try {
      const response = await api.post('/brokers/domains/request_domain/', {
        domain: newDomain.trim(),
      });
      setVerificationInstructions(response.data);
      setNewDomain('');
      toast.success('Domain verification initiated');
      loadDomainSettings();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsRequesting(false);
    }
  };

  /**
   * This only *checks* the DNS record — it never binds the domain. Binding
   * (`Broker.custom_domain`) happens exclusively when a country admin
   * approves the request from their Domain Approvals queue, which runs the
   * same TXT + platform-pointing checks before mutating anything. Reload
   * afterward so a domain a country admin already approved (in the meantime,
   * or before this check ran) shows up as the current domain either way.
   */
  const handleVerifyDomain = async (domain: string) => {
    setVerifyingDomain(domain);
    try {
      const response = await api.post('/brokers/domains/verify_domain/', { domain });
      const { verified, detail } = response.data as { verified: boolean; detail: string };
      if (verified) {
        toast.success(detail || 'DNS looks correct — ask a country admin to approve it.');
      } else {
        toast(detail || 'DNS is not set up correctly yet.', { icon: '⚠️' });
      }
      loadDomainSettings();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setVerifyingDomain(null);
    }
  };

  if (!canManageDomain) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
        <p className="text-gray-500 mt-2">You do not have permission to manage domain settings.</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Domain Settings"
        subtitle="Manage your custom domain for white-label access"
        breadcrumbs={[
          { label: 'Settings', href: '/settings' },
          { label: 'Domain Settings' },
        ]}
      />

      <div className="space-y-6 max-w-2xl">
        {/* Current Domain */}
        <Card>
          <CardHeader title="Current Domain" />
          {isLoading ? (
            <div className="animate-pulse h-8 bg-gray-200 rounded w-64" />
          ) : currentDomain ? (
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Your broker is accessible at: <strong>{currentDomain}</strong>
                </p>
              </div>
              <Badge variant="success" size="sm">Verified</Badge>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <GlobeAltIcon className="w-5 h-5 text-gray-400" />
              <p className="text-sm text-gray-500">No custom domain configured</p>
            </div>
          )}
        </Card>

        {/* Add Custom Domain */}
        <Card>
          <CardHeader
            title="Add Custom Domain"
            subtitle="Point your domain to the platform for branded access"
          />
          <form onSubmit={handleRequestDomain} className="flex gap-3">
            <Input
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="e.g., insurance.yourcompany.com"
              className="flex-1"
            />
            <Button type="submit" isLoading={isRequesting}>
              Request Domain
            </Button>
          </form>
        </Card>

        {/* Verification Instructions */}
        {verificationInstructions && (
          <Card>
            <CardHeader title="DNS Verification Required" />
            <div className="space-y-3">
              <p className="text-sm text-gray-700">{verificationInstructions.instructions}</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">TXT Record Value</p>
                <code className="text-sm text-gray-900 break-all">
                  {verificationInstructions.verification_token}
                </code>
              </div>
              <p className="text-xs text-gray-500">
                After adding the TXT record, click &quot;Check DNS&quot; on the pending verification below to
                confirm it is set up correctly. That check alone does not activate the domain — a country
                admin still needs to approve it. DNS propagation may take up to 24 hours.
              </p>
            </div>
          </Card>
        )}

        {/* Pending Verifications */}
        {pendingVerifications.length > 0 && (
          <Card>
            <CardHeader
              title="Pending Verifications"
              subtitle={`${pendingVerifications.length} domain(s) awaiting country-admin approval`}
            />
            <div className="space-y-3">
              {pendingVerifications.map((v) => (
                <div
                  key={v.domain}
                  className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{v.domain}</p>
                      <p className="text-xs text-gray-500">Token: {v.verification_token.slice(0, 16)}...</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleVerifyDomain(v.domain)}
                    isLoading={verifyingDomain === v.domain}
                  >
                    Check DNS
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        <PublicLookupCard canManage={canManageDomain} />
      </div>
    </div>
  );
}

/**
 * Whether visitors to the broker's public site may buy a registration search.
 *
 * It lives here because it is a property of the public site, not of the
 * broker's own tooling: this is the one control that lets someone the broker
 * has never met spend the broker's money. Off by default, and the cap
 * defaults to zero so the toggle alone does not open the tap.
 *
 * The price and the balance are shown but not editable — the country admin
 * sets the terms. What the broker decides is exposure.
 */
function PublicLookupCard({ canManage }: { canManage: boolean }) {
  const { data: plan, isLoading } = useOwnLookupPlan();
  const save = useSaveOwnLookupPlan();
  const [cap, setCap] = useState<string>('');

  useEffect(() => {
    if (plan?.has_plan) setCap(String(plan.public_daily_cap ?? 0));
  }, [plan]);

  if (isLoading) return null;

  // No plan means the country admin has not set terms for this broker, so
  // there is nothing here to configure and a toggle would only mislead.
  if (!plan?.has_plan || !plan.is_enabled) {
    return (
      <Card>
        <CardHeader
          title="Registration search on your public site"
          subtitle="Not enabled for your account — contact your administrator to set it up."
        />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Registration search on your public site"
        subtitle="Let visitors look up a vehicle by its registration number before asking for a quote."
      />
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Vehicles already in our records are free and always available. Buying a
          record we do not hold costs{' '}
          <span className="font-medium">{formatCurrency(plan.price_per_search ?? 0)}</span> per search
          against your{' '}
          {plan.settlement === 'prepaid'
            ? `balance of ${formatCurrency(plan.balance ?? 0)}`
            : 'account, settled later'}
          . Today&apos;s visitors have used {plan.public_searches_today ?? 0} of{' '}
          {plan.public_daily_cap ?? 0}.
        </p>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            disabled={!canManage || save.isPending}
            checked={!!plan.public_lookup_enabled}
            onChange={(e) => save.mutate({ public_lookup_enabled: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Allow visitors to buy a registration search
          </span>
        </label>

        <div className="sm:max-w-xs">
          <Input
            label="Most searches per day"
            type="number"
            min={0}
            disabled={!canManage || save.isPending}
            value={cap}
            onChange={(e) => setCap(e.target.value)}
            helperText="Zero means none. This is what stops a bot spending your balance overnight."
          />
          <Button
            size="sm"
            className="mt-2"
            disabled={!canManage || save.isPending}
            onClick={() => save.mutate({ public_daily_cap: Number(cap) || 0 })}
          >
            Save daily limit
          </Button>
        </div>
      </div>
    </Card>
  );
}
